import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import store from 'stores/RootStore';

const Container = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface AnimationContainerProps {
  $showBorder?: boolean;
  $scale: number;
}

const AnimationContainer = styled.div<AnimationContainerProps>`
  overflow: visible;
  transform: ${(p) => `scale(${p.$scale})`};
  transform-origin: center center;
  border: ${(p) => (p.$showBorder ? '1px solid #000' : 'none')};
`;

function AnimationStageFrame() {
  const store_animation = store.animation;
  const animationContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!store_animation) return;

    // Captured rather than re-read from the store on teardown. The class
    // version this replaced read store.animation afresh in
    // componentWillUnmount, so if the animation had been swapped in between
    // it destroyed a stranger's player and leaked the one it had actually
    // loaded.  Holding the reference is what makes mount and unmount a
    // pair.
    const player = store_animation.player;

    // One reaction replaces what used to be a mount-time branch on
    // `assets.newest` plus LOADED and CHANGED listeners on the assets
    // collection. `currentAnimationAsset` is a computed over `assets` and
    // `data.currentAnimationID`, so it changes on its own as the collection
    // loads, and fireImmediately covers the already-loaded case the
    // listeners existed for.
    //
    // That also settles the FIXME the class version carried: it seeded from
    // `assets.newest` while its reaction tracked `currentAnimationAsset`,
    // so a remount after a Restore showed the newest version instead of the
    // current one. There is one source of truth now, and it is the current
    // asset.
    //
    // Tracking the URL rather than the asset means it only re-fires when
    // the thing we would actually load has changed.
    const disposeReaction = reaction(
      () => store_animation.currentAnimationAsset?.downloadURL,
      (url) => {
        if (!url) return;
        if (!animationContainerRef.current) {
          console.warn('AnimationStageFrame: no animation container');
          return;
        }
        player.loadAnimation(animationContainerRef.current, url);
      },
      { fireImmediately: true }
    );

    return () => {
      disposeReaction();
      // destroy() runs reset(), which already calls
      // setAnimationContainer(null) - the class version called both.
      player.destroy();
    };
  }, [store_animation]);

  if (!store_animation) return null;

  // Read through the store rather than the captured reference: this is what
  // makes `observer` track showBorder and player.scale.
  return (
    <Container>
      <AnimationContainer
        ref={animationContainerRef}
        $showBorder={store_animation.showBorder}
        $scale={store_animation.player.scale}
      />
    </Container>
  );
}

export default observer(AnimationStageFrame);
